import React, { useRef, useState } from 'react';
import { Avatar, Button, Tabs, Typography, Descriptions, Tag, Dropdown, Menu, Segmented, Form, Select } from 'antd';
import {
    PhoneOutlined,
    MailOutlined,
    EditOutlined,
    DownOutlined,
    SkypeOutlined,
    PlusCircleOutlined,
} from '@ant-design/icons';
import NotesComponent from '../../components/Forms/NotesComponent';
import moment from 'moment';
import timeAgo from '../../constants/functions';
import EditableFormBlock from '../../components/Forms/EditableField';
import { MasterConfigs } from '../../interfaces/entity';
import { constants } from '../../constants/common';
import ProfileCard from '../../components/ContactInfo/ProfileCard';
import Timeline from './Timeline';
import Attachments from './Attachments';
import MailsComponent from './Mails/MailsComponent';

const { Title, Text, Link } = Typography;
const { TabPane } = Tabs;

const LeadDetails: React.FC<any> = ({ lead }) => {
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);
    const [form] = Form.useForm();

    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

    const leadOwnerConfig: MasterConfigs = {
        api: () => '/leads',
        getFormColumns: () => [
            {
                label: 'Lead Owner',
                name: 'leadOwner',
                type: 'select',
                commonProps: {
                    disabled: true,
                },
                render: (text, data) => {
                    return `${text?.firstName} ${text?.lastName}`
                },
                span: 24,
            },
            {
                label: 'Lead Type',
                name: 'LeadsTypes',
                rules: [{ required: true, message: 'Lead Type is required' }],
                type: 'select',
                isCombo: true,
                render: (text, data) => {
                    return text?.name
                },
                span: 24,
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 24,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Phone',
                name: 'phone',
                type: 'text',
                span: 24,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const phoneRegex = /^[0-9]{10,15}$/;
                            return phoneRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid phone number'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 24,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'Lead Status',
                name: 'leadStatus',
                type: 'select',
                span: 24,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.LEAD_STATUS,
            },
        ],
        combo: [
            {
                api: '/leads-types',
                name: 'LeadsTypes',
                idKey: '_id',
                render: (text) => text.name
            }
        ]
    }

    const leadConfig: MasterConfigs = {
        api: () => '/leads',
        getFormColumns: () => [
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Lead Information</Title>
            },
            {
                label: 'Lead Owner',
                name: 'leadOwner',
                type: 'select',
                isCombo: true,
                span: 24,
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                }
            },
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                inputProps: {
                    addonBefore: <Form.Item name="preName">
                        <Select style={{ width: 100 }} placeholder="Title">
                            {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                },
                rules: [{ required: true, message: 'First Name is required' }],
                span: 12,
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
                span: 12,
            },
            {
                label: 'Company',
                name: 'company',
                type: 'text',
                rules: [{ required: true, message: 'Company is required' }],
                span: 12,
            },
            {
                label: 'Title',
                name: 'title',
                type: 'text',
                span: 12,
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Phone',
                name: 'phone',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const phoneRegex = /^[0-9]{10,15}$/;
                            return phoneRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid phone number'));
                        },
                    },
                ],
            },
            {
                label: 'Fax',
                name: 'fax',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const faxRegex = /^[0-9+\-().\s]{6,20}$/;
                            return faxRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid fax number'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'Website',
                name: 'website',
                type: 'text',
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            try {
                                new URL(value);
                                return Promise.resolve();
                            } catch {
                                return Promise.reject(new Error('Enter a valid website URL'));
                            }
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Lead Source',
                name: 'leadSource',
                type: 'select',
                span: 12,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.LEAD_SOURCES
            },
            {
                label: 'Lead Status',
                name: 'leadStatus',
                type: 'select',
                span: 12,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.LEAD_STATUS,
            },
            {
                label: 'Industry',
                name: 'industry',
                type: 'select',
                span: 12,
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                    optionFilterProp: 'children',
                },
                children: constants.INDUSTRIES.map(industry => ({ name: industry, value: industry })),
            },
            {
                label: 'No. of Employees',
                name: 'noOfEmployees',
                type: 'text',
                span: 12,
            },
            {
                label: 'Annual Revenue  ',
                name: 'annualRevenue',
                type: 'text',
                render: (text) => {
                    return text ? `INR ${text}` : null;
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const revenueRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                            return revenueRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid annual revenue'));
                        }
                    },
                ],
                inputProps: {
                    prefix: 'INR ',
                },
                span: 12,
            },
            {
                label: 'Rating',
                name: 'rating',
                type: 'select',
                span: 12,
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                    optionFilterProp: 'children',
                },
                children: constants.RATINGS.map(rating => ({ name: rating, value: rating })),
            },
            {
                label: 'Email Opt Out ',
                name: 'emailOptOut',
                type: 'checkbox',
                span: 12,
                render: (text) => {
                    return text ? 'Yes' : 'No';
                },
            },
            {
                label: 'Secondary Email',
                name: 'secondaryEmail',
                span: 12,
                type: 'text',
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: "Workflow In Progress",
                name: "workFlowinProgress",
                type: "checkbox",
                span: 12,
                render: (text) => {
                    return text ? 'Yes' : 'No';
                },
            },
            {
                label: 'Twitter',
                name: 'twitter',
                type: 'text',
                span: 12,
            },
            {
                label: 'LinkedIn Profile URL (User)',
                name: 'linkedinProfileUrl',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            try {
                                const url = new URL(value);
                                if (url.hostname.includes('linkedin.com')) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                            } catch {
                                return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                            }
                        },
                    },
                ],
                render: (text) => {
                    if (!text) return 'N/A';
                    return <Link href={text} target="_blank" rel="noopener noreferrer">{text}</Link>;
                },
            },
            {
                label: 'LinkedIn Profile URL (Company)',
                name: 'companyLinkedinProfileUrl',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            try {
                                const url = new URL(value);
                                if (url.hostname.includes('linkedin.com')) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                            } catch {
                                return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                            }
                        },
                    },
                ],
                render: (text) => {
                    if (!text) return 'N/A';
                    return <Link href={text} target="_blank" rel="noopener noreferrer">{text}</Link>;
                },
            },
            {
                label: 'Workflow Active',
                name: 'workFlowActive',
                type: 'checkbox',
                span: 12,
                render: (text) => {
                    return text ? 'Yes' : 'No';
                },
            },
            {
                label: 'Modified By',
                name: 'updatedBy',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {moment(text?.updatedAt).format('llll')}
                        </Text>
                    </div>
                },
                span: 12,
            },
            {
                label: 'Created By',
                name: 'createdBy',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {moment(text?.createdAt).format('llll')}
                        </Text>
                    </div>
                },
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Address Information</Title>
            },
            {
                label: 'Street',
                name: ['address', 'street'],
                type: 'text',
                render: (text) => text || 'N/A',
                span: 12,
            },
            {
                label: 'City',
                name: ['address', 'city'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
            },
            {
                label: 'State',
                name: ['address', 'state'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
            },
            {
                label: 'Zip Code',
                name: ['address', 'zip'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const zipRegex = /^[0-9]{4,10}$/;
                            return zipRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid zip code'));
                        },
                    },
                ],
            },
            {
                label: 'Country',
                type: 'text',
                name: ['address', 'country'],
                render: (text) => text || 'N/A',
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 }
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'leadOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            }
        ],
    }

    const sections = [
        { id: 'notes', title: 'Notes' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'products', title: 'Products' },
        { id: 'open-activities', title: 'Open Activities' },
        { id: 'closed-activities', title: 'Closed Activities' },
        { id: 'invited-meetings', title: 'Invited Meetings' },
        { id: 'emails', title: 'Emails', notInclude: true },
        { id: 'campaigns', title: 'Campaigns' },
        { id: 'social', title: 'Social' },
    ];


    const scrollTo = (id: string) => {
        const section = sectionRefs.current[id];
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(id); // Highlight

            setTimeout(() => {
                setHighlighted(current => (current === id ? null : current));
            }, 1000 * 5);
        }
    };

    return (
        <div className="flex overflow-hidden">
            <div className="w-64 bg-white shadow-sm border-r  h-[calc(100vh-60px)] p-4 flex-shrink-0">
                <Text strong className="block mb-4">Related List</Text>
                <ul className="text-sm">
                    {sections.map(section => (
                        <li
                            key={section.id}
                            onClick={() => scrollTo(section.id)}
                            className="cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-xm group"
                        >
                            <div className='flex items-center justify-between h-5'>
                                {section.title}
                                <Button type='text' className='hidden group-hover:block' icon={<PlusCircleOutlined className='pt-1' />} size="small" />
                            </div>
                        </li>
                    ))}

                    <li className="text-blue-600 cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-sm">Add Related List</li>
                </ul>
                <Text strong className="block mt-6 mb-2 ">Links</Text>
                <p className="text-blue-600 cursor-pointer text-sm hover:bg-gray-100 py-2 px-2 rounded-sm">Add Link</p>
            </div>

            <div className="flex-1 overflow-y-auto h-full max-h-[calc(100vh-60px)] p-2">
                <div className="flex-1 p-2">
                    <div className='flex items-center justify-between mb-4 '>
                        <Segmented
                            options={[
                                { label: 'Overview', value: 'overview' },
                                { label: 'Timeline', value: 'timeline' },
                            ]}
                            value={activeTab}
                            onChange={value => setActiveTab(value as 'overview' | 'timeline')}
                        />
                        <div>
                            <Text className='text-sm text-gray-500'>Last Update : {timeAgo(lead?.updatedAt)}</Text>
                        </div>
                    </div>

                    {activeTab === 'overview' && (<div className='space-y-4'>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={leadOwnerConfig}
                                        id={lead?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={leadConfig}
                                        id={lead?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>

                        {sections.map(section => (
                            !section?.notInclude && <div
                                key={section.id}
                                ref={el => (sectionRefs.current[section.id] = el)}
                                className="bg-white rounded-md mb-6"
                            >
                                {!['Attachments'].includes(section.title) && <div className="flex justify-between items-center py-2 px-4 border-b">
                                    <Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>{section.title}</Title>
                                    {section.title !== 'Notes' && <Button type="text" disabled>Add {section.title}</Button>}
                                </div>}
                                {!['Notes', 'Attachments'].includes(section.title) && <div className="p-4 text-gray-300 text-center">No {section.title.toLowerCase()} available</div>}
                                {section.title === 'Notes' && <NotesComponent refType={'Leads'} refId={lead?._id} />}
                                {section.title === 'Attachments' && <div className='p-4'><Attachments refType={'Leads'} refId={lead?._id} /> </div>}
                            </div>
                        ))}
                        <MailsComponent Email={lead?.email} sectionRefs={sectionRefs} highlighted={highlighted} />
                    </div>)}
                    {activeTab === 'timeline' && <Timeline type="Leads" id={lead?._id} />}
                </div>
            </div>
        </div>
    );
};

export default LeadDetails;